import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  constructor(private translate: TranslateService) { }

  private createModal(type: 'delete' | 'error' | 'success' | 'confirmation', options?: { title?: string, message?: string, showCancel?: boolean, confirmButtonText?: string, cancelButtonText?: string, titleKey?: string, messageKey?: string, confirmButtonTextKey?: string, cancelButtonTextKey?: string }): Promise<boolean> {
    const title = options?.titleKey ? this.translate.instant(options.titleKey) : options?.title || '';
    const message = options?.messageKey ? this.translate.instant(options.messageKey) : options?.message || '';
    const showCancel = options?.showCancel || false;
    const confirmButtonText = options?.confirmButtonTextKey ? this.translate.instant(options.confirmButtonTextKey) : options?.confirmButtonText;
    const cancelButtonText = options?.cancelButtonTextKey ? this.translate.instant(options.cancelButtonTextKey) : options?.cancelButtonText;

    return new Promise((resolve) => {
      // Create overlay
      const overlay = document.createElement('div');
      overlay.className = 'custom-alert-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      `;

      // Create modal
      const modal = document.createElement('div');
      modal.className = 'custom-alert-modal';
      
      const colors = {
        delete: { bg: '#FFE5E5', border: '#FF6B6B', icon: '/assets/icons/del.jfif' },
        error: { bg: '#FFE5E5', border: '#FF4444', icon: '/assets/icons/error-icon.svg' },
        success: { bg: '#E8F5E8', border: '#4CAF50', icon: '/assets/icons/success-icon.svg' },
        confirmation: { bg: '#E8F5E8', border: '#4CAF50', icon: '/assets/icons/success-icon.svg' }
      };

      modal.style.cssText = `
        background: #fff;
        border: 3px solid ${colors[type].border};
        border-radius: 20px;
        padding: 30px;
        max-width: 400px;
        width: 90%;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;

      // Create icon
      const icon = document.createElement('img');
      icon.src = colors[type].icon;
      icon.style.cssText = `
        width: 60px;
        height: 60px;
        margin-bottom: 20px;
        display: block;
        margin-left: auto;
        margin-right: auto;
        border-radius: 50%;
        overflow: hidden;
      `;

      // Create title
      const titleElement = document.createElement('h2');
      titleElement.textContent = title;
      titleElement.style.cssText = `
        margin: 0 0 15px 0;
        font-size: 24px;
        font-weight: 600;
        color: #333;
      `;

      // Create message
      const messageElement = document.createElement('p');
      messageElement.textContent = message;
      messageElement.style.cssText = `
        margin: 0 0 25px 0;
        font-size: 16px;
        color: #666;
        line-height: 1.4;
      `;

      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = `
        display: flex;
        gap: 15px;
        justify-content: center;
      `;

      // Create confirm button
      const confirmButton = document.createElement('button');
      const confirmTexts = {
        delete: 'Valider',
        error: 'Fermer',
        success: 'Fermer',
        confirmation: 'Valider'
      };
      confirmButton.textContent = confirmButtonText || confirmTexts[type]; // Adjusted for search/replace process
      confirmButton.style.cssText = `
        background: ${type === 'delete' ? 'linear-gradient(90deg, #F07D00 0%, #FC0005 100%)' : type === 'error' ? 'linear-gradient(90deg, #F07D00 0%, #FC0005 100%)' : 'linear-gradient(90deg, #0ea5a6 0%, #b3ea82 100%)'};
        color: white;
        border: none;
        border-radius: 25px;
        padding: 12px 30px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 100px;
      `;

      confirmButton.onmouseover = () => {
        confirmButton.style.opacity = '0.9';
        confirmButton.style.transform = 'translateY(-1px)';
      };
      confirmButton.onmouseout = () => {
        confirmButton.style.opacity = '1';
        confirmButton.style.transform = 'translateY(0)';
      };

      confirmButton.onclick = () => {
        document.body.removeChild(overlay);
        resolve(true);
      };

      buttonContainer.appendChild(confirmButton);

      // Create cancel button if needed
      if (showCancel) {
        const cancelButton = document.createElement('button');
        cancelButton.textContent = cancelButtonText || 'Annuler';
        cancelButton.style.cssText = `
          background: linear-gradient(#fff, #fff) padding-box, ${type === 'delete' || type === 'error' ? 'linear-gradient(90deg, #F07D00 0%, #FC0005 100%)' : 'linear-gradient(90deg, #0ea5a6 0%, #b3ea82 100%)'} border-box;
          background-origin: border-box;
          background-clip: padding-box, border-box;
          color: #666;
          border-radius: 25px;
          border: 2px solid transparent;
          padding: 12px 30px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 100px;
        `;

        cancelButton.onmouseover = () => {
          cancelButton.style.cssText = `
            background: linear-gradient(#f5f5f5, #f5f5f5) padding-box, ${type === 'delete' || type === 'error' ? 'linear-gradient(90deg, #F07D00 0%, #FC0005 100%)' : 'linear-gradient(90deg, #0ea5a6 0%, #b3ea82 100%)'} border-box;
            background-origin: border-box;
            background-clip: padding-box, border-box;
            color: #666;
            border-radius: 25px;
            border: 2px solid transparent;
            padding: 12px 30px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 100px;
          `;
          cancelButton.style.transform = 'translateY(-1px)';
        };
        
        cancelButton.onmouseout = () => {
          cancelButton.style.cssText = `
            background: linear-gradient(#fff, #fff) padding-box, ${type === 'delete' || type === 'error' ? 'linear-gradient(90deg, #F07D00 0%, #FC0005 100%)' : 'linear-gradient(90deg, #0ea5a6 0%, #b3ea82 100%)'} border-box;
            background-origin: border-box;
            background-clip: padding-box, border-box;
            color: #666;
            border-radius: 25px;
            border: 2px solid transparent;
            padding: 12px 30px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 100px;
          `;
          cancelButton.style.transform = 'translateY(0)';
        };

        cancelButton.onclick = () => {
          document.body.removeChild(overlay);
          resolve(false);
        };

        buttonContainer.appendChild(cancelButton);
      }

      // Assemble modal
      modal.appendChild(icon);
      modal.appendChild(titleElement);
      modal.appendChild(messageElement);
      modal.appendChild(buttonContainer);
      overlay.appendChild(modal);

      // Add to DOM
      document.body.appendChild(overlay);

      // Close on overlay click
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          resolve(false);
        }
      };
    });
  }

  // Alert de suppression
  showDeleteConfirmation(options?: { title?: string, message?: string, confirmButtonText?: string, cancelButtonText?: string, titleKey?: string, messageKey?: string, confirmButtonTextKey?: string, cancelButtonTextKey?: string }): Promise<boolean> {
    const defaultOptions = {
      showCancel: true,
      titleKey: 'ALERTS.DELETE_CONFIRMATION_TITLE',
      messageKey: 'ALERTS.DELETE_CONFIRMATION_MESSAGE',
      confirmButtonTextKey: 'ALERTS.DELETE_CONFIRMATION_CONFIRM_BUTTON',
      cancelButtonTextKey: 'ALERTS.DELETE_CONFIRMATION_CANCEL_BUTTON'
    };
    return this.createModal('delete', { ...defaultOptions, ...options });
  }

  // Alert d'erreur
  showError(options?: { title?: string, message?: string, confirmButtonText?: string, titleKey?: string, messageKey?: string, confirmButtonTextKey?: string }): Promise<boolean> {
    const defaultOptions = {
      showCancel: false,
      titleKey: 'ALERTS.ERROR_TITLE',
      messageKey: 'ALERTS.ERROR_MESSAGE',
      confirmButtonTextKey: 'ALERTS.ERROR_CONFIRM_BUTTON'
    };
    return this.createModal('error', { ...defaultOptions, ...options });
  }

  // Alert de succès
  showSuccess(options?: { title?: string, message?: string, confirmButtonText?: string, titleKey?: string, messageKey?: string, confirmButtonTextKey?: string }): Promise<boolean> {
    const defaultOptions = {
      showCancel: false,
      titleKey: 'ALERTS.SUCCESS_TITLE',
      messageKey: 'ALERTS.SUCCESS_MESSAGE',
      confirmButtonTextKey: 'ALERTS.SUCCESS_CONFIRM_BUTTON'
    };
    return this.createModal('success', { ...defaultOptions, ...options });
  }

  // Alert de confirmation
  showConfirmation(options?: { title?: string, message?: string, confirmButtonText?: string, cancelButtonText?: string, titleKey?: string, messageKey?: string, confirmButtonTextKey?: string, cancelButtonTextKey?: string }): Promise<boolean> {
    const defaultOptions = {
      showCancel: true,
      titleKey: 'ALERTS.CONFIRMATION_TITLE',
      messageKey: 'ALERTS.CONFIRMATION_MESSAGE',
      confirmButtonTextKey: 'ALERTS.CONFIRMATION_CONFIRM_BUTTON',
      cancelButtonTextKey: 'ALERTS.CONFIRMATION_CANCEL_BUTTON'
    };
    return this.createModal('confirmation', { ...defaultOptions, ...options });
  }
}